/* ── Paid-session registry (per registration) ───────────────────────────
 * Stores `{ sessionId, purpose, ts }` keyed by upper-cased registration.
 * Used so a re-search for the SAME car the user actually paid for stays
 * unlocked (we forward the session_id to /api/analyze/status & /api/report
 * so the backend serves the full payload), while a re-search for a DIFFERENT
 * registration — or the same one without a recorded paid session — falls
 * back to the paywall. Prevents the previous frontend leak where any user
 * who ever paid for any reg saw premium on every subsequent search.
 */
import { getServiceModeSession } from "./serviceMode";

/** Debug-level trace for intentionally non-fatal storage failures
 *  (private-mode Safari, blocked storage, corrupt JSON). */
const logStorageError = (context, err) => {
  try { console.debug(`[paidSessions] ${context}:`, err); } catch { /* console unavailable */ }
};

export const getPaidSession = (reg) => {
  try {
    const all = JSON.parse(localStorage.getItem('worthifyPaidSessions') || '{}');
    const perReg = all[(reg || '').toString().toUpperCase().trim()] || null;
    if (perReg) return perReg;
    // Fallback: service-mode session unlocks ANY registration for its 24h window.
    const svc = getServiceModeSession();
    if (svc && svc.sessionId) {
      return { sessionId: svc.sessionId, purpose: "pdf", ts: svc.activatedAt };
    }
    return null;
  } catch (e) { logStorageError('read failed', e); return null; }
};

export const setPaidSession = (reg, sessionId, purpose) => {
  if (!reg || !sessionId) return;
  try {
    const all = JSON.parse(localStorage.getItem('worthifyPaidSessions') || '{}');
    all[reg.toString().toUpperCase().trim()] = { sessionId, purpose, ts: Date.now() };
    localStorage.setItem('worthifyPaidSessions', JSON.stringify(all));
  } catch (e) { logStorageError('write failed', e); }
};

/**
 * On app boot, validate every stored paid-session against the backend. Any
 * entry whose Stripe session is no longer paid (test artefact, expired,
 * refunded, never completed) gets pruned. Runs once per page-load so the
 * paywall always reflects real Stripe state — not stale localStorage from
 * previous testing or pre-launch flows.
 */
export const cleanupStalePaidSessions = async () => {
  let all;
  try { all = JSON.parse(localStorage.getItem('worthifyPaidSessions') || '{}'); } catch { return; }
  const regs = Object.keys(all || {});
  if (regs.length === 0) return;
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (!backend) return;
  const next = { ...all };
  let changed = false;
  await Promise.all(regs.map(async (reg) => {
    const entry = all[reg];
    const sid = entry?.sessionId;
    if (!sid) { delete next[reg]; changed = true; return; }
    try {
      const r = await fetch(`${backend}/api/payment-status/${encodeURIComponent(sid)}`, { method: 'GET' });
      // ── Bug fix (Feb 2026): only delete on an EXPLICIT non-paid response.
      // Previously a 500/429/503 from Stripe (rate limit, transient error)
      // would silently delete a perfectly valid paid session, forcing the
      // user to pay £4.99 again. Now we only delete when the backend
      // affirmatively says payment_status !== 'paid'. Any HTTP error path
      // is treated as "transient" and we keep the entry untouched.
      if (!r.ok) return; // KEEP entry — re-validate on next boot
      const j = await r.json().catch(() => null);
      if (!j) return;   // KEEP entry on unparseable body
      if (j.payment_status && j.payment_status !== 'paid') {
        delete next[reg]; changed = true;
      }
    } catch (e) {
      // Network error — keep the entry; we'll revalidate on next boot.
      logStorageError('payment-status check failed (transient, entry kept)', e);
    }
  }));
  if (changed) {
    try { localStorage.setItem('worthifyPaidSessions', JSON.stringify(next)); } catch (e) { logStorageError('prune write failed', e); }
  }
};
